import React from "react";
import PageWithHeader from "../common/PageWithHeader";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Tooltip,
  styled,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  Assignment,
  LocalShipping,
  RateReview,
  Description,
  Home,
  Badge,
  Visibility,
  PrecisionManufacturing,
  School,
  SupervisorAccount,
  AccountBalance,
  ManageAccounts,
  Dashboard as DashboardIcon,
  Engineering,
  AutoAwesome,
} from "@mui/icons-material";
import { useAppSelector } from "../../redux/hooks";
import LoggedOutView from "../common/LoggedOutView";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const StyledCard = styled(Card)(() => ({
  height: "230px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#1976d2",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
  transition: "0.3s",
  borderRadius: "15px",
  padding: "16px",
  "&:hover": {
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
    transform: "scale(1.01)",
  },
}));

const DisabledCard = styled(Card)(() => ({
  height: "230px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#b0b0b0",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: "15px",
  padding: "16px",
}));

const StyledTypography = styled(Typography)(() => ({
  textAlign: "center",
  color: "#fff",
  fontSize: "1.25rem",
}));

const Dashboard: React.FC = () => {
  const user = useAppSelector((state) => state.auth.user);
  const currentUser = useAppSelector((state) => state.auth.user);

  const userAccess = {
    invite: !!(currentUser && currentUser.roles?.includes("vms")),
    procurement: !!(currentUser && currentUser.roles?.includes("procurement")),
    reviews: true,
    agreements: !!(currentUser && currentUser.roles?.includes("agreement")),
    payroll: !!(currentUser && currentUser.roles?.includes("payroll")),
    propertyRegister: !!(
      currentUser && currentUser.roles?.includes("propertyRegister")
    ),
    accessControl: true,
    cSuite: !!(currentUser && currentUser.roles?.includes("cSuite")),
    siteInspector: !!(
      currentUser && currentUser.roles?.includes("siteInspector")
    ),
    wdc: !!(currentUser && currentUser.roles?.includes("admin")),
    admin: !!(currentUser && currentUser.roles?.includes("admin")),
    finance: !!(
      (currentUser && currentUser.roles?.includes("finance")) ||
      currentUser?.roles?.includes("finance-manager") ||
      currentUser?.roles?.includes("admin")
    ),
    staffManagement: !!(
      currentUser &&
      currentUser.roles?.some((r) =>
        ["staffManagement", "Human Resources", "hr", "admin"].includes(r),
      )
    ),
  };

  const options = [
    {
      id: 0,
      title: "C Suite AI Assistant",
      link: `/ai-assistant`,
      icon: <AutoAwesome sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.cSuite || userAccess.admin,
    },
    {
      id: 1,
      title: "Knowledge Base",
      link: `/knowledge-base`,
      icon: <School sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: true,
    },
    {
      id: 2,
      title: "Performance System",
      link: `/reviews`,
      icon: <RateReview sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.reviews,
    },
    {
      id: 3,
      title: "Procurement",
      link: `/procurement`,
      icon: <LocalShipping sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.procurement,
    },
    {
      id: 4,
      title: "Invite Management",
      link: `/invite`,
      icon: <Assignment sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.invite,
    },
    {
      id: 5,
      title: "Agreements",
      link: `/agreements`,
      icon: <Description sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.agreements,
    },
    {
      id: 6,
      title: "Payroll",
      link: `/payroll`,
      icon: <AttachMoneyIcon sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.payroll,
    },
    {
      id: 7,
      title: "Property Register",
      link: `/property-register`,
      icon: <Home sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.propertyRegister,
    },
    {
      id: 8,
      title: "Site Access",
      link: `/access-control`,
      icon: <Badge sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.accessControl,
    },
    {
      id: 9,
      title: "Site Inspections",
      link: `/site-inspections`,
      icon: <Visibility sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.siteInspector,
    },
    {
      id: 10,
      title: "Plant Reports",
      link: `/plant-reports`,
      icon: (
        <PrecisionManufacturing sx={{ fontSize: 60, mb: 1, color: "#fff" }} />
      ),
      access: userAccess.wdc,
    },
    {
      id: 11,
      title: "User Management",
      link: `/admin/users`,
      icon: <SupervisorAccount sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.admin,
    },
    {
      id: 12,
      title: "Finance",
      link: `/finance`,
      icon: <AccountBalance sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: userAccess.finance,
    },
    // DISABLED: Staff Management module
    // {
    //   id: 15,
    //   title: "Staff Management",
    //   link: `/staff-management`,
    //   icon: <ManageAccounts sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
    //   access: userAccess.staffManagement,
    // },
    {
      id: 13,
      title: "Wallboard",
      link: `/wallboard`,
      icon: <DashboardIcon sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: true,
    },
    {
      id: 14,
      title: "COMS",
      link: `/coms`,
      icon: <Engineering sx={{ fontSize: 60, mb: 1, color: "#fff" }} />,
      access: true,
    },
  ];

  if (!user) {
    return <LoggedOutView />;
  }

  return (
    <PageWithHeader>
      <Box m={5} display="flex" flexDirection="column" alignItems="center">
        <Typography
          variant="h1"
          gutterBottom
          style={{
            fontWeight: "bold",
            color: "#123456",
            fontSize: "2.5rem",
            textAlign: "center",
          }}
        >
          WestProp Operations Hub
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          {options.map((option) => (
            <Grid item xs={12} sm={6} md={4} key={option.id}>
              {option.access ? (
                <Link
                  to={option.link}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "block",
                  }}
                >
                  <StyledCard>
                    <CardContent style={{ textAlign: "center" }}>
                      {option.icon}
                      <StyledTypography variant="h6">
                        {option.title}
                      </StyledTypography>
                    </CardContent>
                  </StyledCard>
                </Link>
              ) : (
                <Tooltip title="You do not have access to this function" arrow>
                  <Box>
                    <DisabledCard>
                      <CardContent style={{ textAlign: "center" }}>
                        {option.icon}
                        <StyledTypography>{option.title}</StyledTypography>
                      </CardContent>
                    </DisabledCard>
                  </Box>
                </Tooltip>
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
    </PageWithHeader>
  );
};

export default Dashboard;
